// Google Ad Manager (GAM) ad-slot definitions. Pure data — no GPT / DOM access here.
//
// Each placement maps to a GAM ad unit (path = `/<networkCode>/<adUnitPath>`, the network code comes
// from the `seavien-ads-network-code` meta) plus the sizes we request and a fixed reserved box so the
// slot never shifts layout (CLS) before or after it fills. See
// designs/04-live/2026-06-22-google-ad-manager-display-ads-design.md.

export type AdPlacementKey = "home-infeed" | "rail-rectangle" | "discussion-incontent";

/** A GPT creative size: a [width, height] pair, or "fluid" for native/responsive fluid slots. */
export type AdSize = [number, number] | "fluid";

/**
 * One responsive size-mapping rule: when the viewport is at least [width, height], request `sizes`.
 * GPT picks the rule with the largest matching viewport. An empty `sizes` array means "no ad at this
 * viewport".
 */
export type AdResponsiveSizeMapping = {
    viewport: [number, number];
    sizes: AdSize[];
};

export type AdPlacementDefinition = {
    key: AdPlacementKey;
    /** Ad unit path suffix appended after the network code (GAM ad unit name). */
    adUnitPath: string;
    /** Superset of every size this slot may serve (across all viewports). */
    sizes: AdSize[];
    /** Responsive size mapping (largest matching viewport wins). */
    sizeMapping: AdResponsiveSizeMapping[];
    /**
     * Tailwind min-height classes reserving the slot's box BEFORE it fills, sized to the tallest
     * creative per breakpoint. This is the core anti-CLS guarantee.
     */
    reservedClassName: string;
};

// Viewport breakpoints used for responsive mapping (match the app's lg breakpoint ~1024px).
const DESKTOP_VIEWPORT: [number, number] = [1024, 0];
const MOBILE_VIEWPORT: [number, number] = [0, 0];

export const AD_PLACEMENTS: Record<AdPlacementKey, AdPlacementDefinition> = {
    // In-feed unit between discussion cards on the home feed. Fluid + a medium rectangle on mobile,
    // fluid + leaderboard on desktop. Reserved to the tallest expected creative per breakpoint.
    "home-infeed": {
        key: "home-infeed",
        adUnitPath: "seavien_home_infeed",
        sizes: ["fluid", [300, 250], [728, 90]],
        sizeMapping: [
            { viewport: DESKTOP_VIEWPORT, sizes: ["fluid", [728, 90]] },
            { viewport: MOBILE_VIEWPORT, sizes: ["fluid", [300, 250]] },
        ],
        reservedClassName: "min-h-[280px] lg:min-h-[140px]",
    },

    // Sticky rectangle in the desktop-only right rail. Never shifts content (lateral column).
    "rail-rectangle": {
        key: "rail-rectangle",
        adUnitPath: "seavien_rail_rectangle",
        sizes: [[300, 250]],
        sizeMapping: [
            { viewport: DESKTOP_VIEWPORT, sizes: [[300, 250]] },
            // Rail is hidden below lg; no ad on mobile from this placement.
            { viewport: MOBILE_VIEWPORT, sizes: [] },
        ],
        reservedClassName: "min-h-[250px]",
    },

    // In-content unit between argument sections on a discussion page.
    "discussion-incontent": {
        key: "discussion-incontent",
        adUnitPath: "seavien_discussion_incontent",
        sizes: ["fluid", [300, 250], [728, 90]],
        sizeMapping: [
            { viewport: DESKTOP_VIEWPORT, sizes: ["fluid", [728, 90]] },
            { viewport: MOBILE_VIEWPORT, sizes: ["fluid", [300, 250]] },
        ],
        reservedClassName: "min-h-[280px] lg:min-h-[140px]",
    },
};
